import { Autocomplete, Box, TextField } from "@mui/material";
import { useHunterSearchQuery } from "Api/Queries/User/useHunterSearchQuery";
import {
  getHunterOptionLabel,
  isHunterLicenseInactive,
  isHunterOptionEqualToValue,
  renderHunterOption,
  sortHuntersByLicenseActivity,
} from "Components/Shared/Inputs/hunterAutocompleteHelpers";
import { useDebouncedSearch } from "Hooks/useDebouncedSearch";
import type { HunterSearchDto, Region } from "Infrastructure/Api/Api";
import { useMemo, useState } from "react";
import { Resources, useResource } from "Translations/Resources";

const COMPONENT_RESOURCES = Resources.Components.Shared.HunterAutocomplete;

type Props = {
  selectedHunter: HunterSearchDto | null;
  onHunterChange: (hunter: HunterSearchDto | null) => void;
  disabled?: boolean;
  label?: string;
  region?: Region | null;
  isHunterWithoutLicenseIncluded?: boolean;
  excludedLicensePublicID?: string;
  excludedPartyPublicID?: string;
};

export const HunterSingleSelectAutocomplete = ({
  selectedHunter,
  onHunterChange,
  disabled = false,
  label,
  region,
  isHunterWithoutLicenseIncluded = false,
  excludedLicensePublicID,
  excludedPartyPublicID,
}: Props) => {
  const { t } = useResource();
  const [inputValue, setInputValue] = useState("");
  const { searchTerm, setSearchTerm, debouncedSearchTerm } =
    useDebouncedSearch();

  const resolvedLabel = label ?? t(COMPONENT_RESOURCES.SearchHunter);

  const { data: searchResults, isLoading } = useHunterSearchQuery(
    debouncedSearchTerm,
    debouncedSearchTerm.length >= 2,
    region,
    isHunterWithoutLicenseIncluded,
  );

  const sortedResults = useMemo(() => {
    const options = (searchResults ?? []).filter(
      (hunter) =>
        hunter.licensePublicID !== excludedLicensePublicID &&
        (!excludedPartyPublicID ||
          hunter.partyPublicID !== excludedPartyPublicID),
    );
    return sortHuntersByLicenseActivity(options);
  }, [searchResults, excludedLicensePublicID, excludedPartyPublicID]);

  // In transfer mode (hunters without a license included) the target's own license status is
  // irrelevant — any active hunter may RECEIVE a license — so nothing is disabled. In the
  // assign-merchant flow a share requires an active license, so inactive rows stay disabled.
  const getIsOptionDisabled = (option: HunterSearchDto) =>
    isHunterWithoutLicenseIncluded ? false : isHunterLicenseInactive(option);

  const handleInputChange = (_e: unknown, newInputValue: string) => {
    setInputValue(newInputValue);
    setSearchTerm(newInputValue);
  };

  const noOptionsText =
    searchTerm.length < 2
      ? t(COMPONENT_RESOURCES.SearchHunterMinLength)
      : t(COMPONENT_RESOURCES.SearchHunterNoResults);

  return (
    <Box>
      {/** biome-ignore lint/a11y/noLabelWithoutControl: associated by wrapping the input */}
      <label>
        {resolvedLabel}
        <Autocomplete<HunterSearchDto, false, false, false>
          options={sortedResults}
          value={selectedHunter}
          onChange={(_e, newValue) => onHunterChange(newValue)}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          loading={isLoading}
          disabled={disabled}
          filterOptions={(x) => x}
          noOptionsText={noOptionsText}
          getOptionLabel={getHunterOptionLabel}
          getOptionDisabled={getIsOptionDisabled}
          isOptionEqualToValue={isHunterOptionEqualToValue}
          renderOption={renderHunterOption}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t(COMPONENT_RESOURCES.SearchHunterPlaceholder)}
              sx={{
                mt: 1,
                "& .MuiOutlinedInput-root": {
                  height: "44px",
                  "& input": {
                    height: "12px",
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "divider",
                  borderRadius: 1,
                },
              }}
            />
          )}
        />
      </label>
    </Box>
  );
};
